import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Search, User as UserIcon, MessageCircle, ClipboardList, Phone, Mail, Package, MapPin, Send, X, ChevronRight, Loader2, Ban, ShieldOff } from 'lucide-react';
import api from '@/lib/api';
import { SellerOrderDetail } from './SellerOrdersSection';
import { getTrackingAvailable, getTrackingUrl, getTrackingStatus } from '@/lib/trackingUrl';

// Phase 12 · Customer Management (replaces the previous COMING SOON placeholder).
// Contains three tabs: 고객목록 / CS 문의 / CS 처리이력.
// Absolutely NO changes to sidebar structure or other sections.

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

// Prod-CS-Polish · 전화번호 마스킹 (리스트 표시용 · 상세는 원본 유지 · CS 업무 필요성).
// 원본 데이터 무변경 · UI 표시 계층에서만 처리. customer_key(phone:{digits}) 로직 무영향.
const maskPhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 8) return phone;
  // 010-****-1234 (앞 3, 뒤 4 노출 · 중간 마스킹)
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
};
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('ko-KR', { hour12: false }); } catch { return '-'; }
};
const digits = (s) => (s || '').replace(/\D/g, '');
const CATEGORY_LABEL = {
  product: '상품문의', order: '주문문의', payment: '결제문의', shipping: '배송문의',
  exchange: '교환문의', return: '반품문의', cancel: '취소문의', other: '기타문의',
};
const STATUS_LABEL = {
  new: '신규', in_progress: '처리중', answered: '답변완료', closed: '종료',
};
const ORDER_STATUS_LABEL = {
  payment_pending: '결제대기', paid: '결제완료', preparing: '배송준비',
  shipping: '배송중', delivered: '배송완료', completed: '주문완료',
  cancelled: '취소', refunded: '환불',
};

// ── Root ──────────────────────────────────────────────────────────────────
export default function CustomersSection({ initialCustomerKey } = {}) {
  const [tab, setTab] = useState('list'); // list | inquiries | history
  const [detailKey, setDetailKey] = useState(initialCustomerKey || null); // selected customer_key
  const [inquiryId, setInquiryId] = useState(null);

  // STEP OMS-OUTBOUND-UNIFY-2 · 외부(주문상세 · 글로벌 검색)에서 customer_key 를
  //   넘겨받으면 자동으로 detail drawer 오픈 · 기존 Customer 360 UI 그대로 REUSE.
  useEffect(() => {
    if (initialCustomerKey) {
      setTab('list');
      setDetailKey(initialCustomerKey);
    }
  }, [initialCustomerKey]);

  return (
    <div className="space-y-4" data-testid="customers-section">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>고객관리</h2>
      </div>

      <div className="flex gap-1 border-b border-black/10 dark:border-white/10">
        {[
          { k: 'list', label: '고객 목록', icon: UserIcon },
          { k: 'inquiries', label: 'CS 문의', icon: MessageCircle },
          { k: 'history', label: 'CS 처리이력', icon: ClipboardList },
        ].map((t) => (
          <button key={t.k} onClick={() => { setTab(t.k); setDetailKey(null); setInquiryId(null); }}
            data-testid={`cust-tab-${t.k}`}
            className={`px-3 py-2 text-xs font-black inline-flex items-center gap-1 border-b-2 -mb-px ${tab === t.k ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500'}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'list' && <CustomerList onOpen={setDetailKey} />}
      {tab === 'inquiries' && <InquiryList onOpen={setInquiryId} onOpenCustomer={setDetailKey} />}
      {tab === 'history' && <HistoryList />}

      {detailKey && <CustomerDetailDrawer customerKey={detailKey} onClose={() => setDetailKey(null)} onOpenInquiry={setInquiryId} />}
      {inquiryId && <InquiryDetailDrawer inquiryId={inquiryId} onClose={() => setInquiryId(null)} onOpenCustomer={setDetailKey} />}
    </div>
  );
}

// ── Customer List ─────────────────────────────────────────────────────────
function CustomerList({ onOpen }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'normal' | 'blocked' · STEP 5-CB
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/seller/customers', { params: { q: q || undefined, sort, status: statusFilter || undefined } });
      setRows(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || '불러오기 실패'); }
    finally { setLoading(false); }
  }, [q, sort, statusFilter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-3 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="이름 · 휴대폰(01012345678) · 이메일 · 회원ID"
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
            data-testid="cust-list-search" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
          data-testid="cust-list-sort">
          <option value="recent">최근 주문순</option>
          <option value="orders">주문 많은순</option>
          <option value="amount">구매금액순</option>
          <option value="unanswered">미처리 문의순</option>
        </select>
        <button onClick={load} className="px-3 py-2 text-xs font-bold rounded-lg bg-black text-white" data-testid="cust-list-refresh">
          검색
        </button>
      </div>

      {/* STEP 5-CB · 상태 필터 (전체/정상/차단) · 기존 UI 스타일 재사용 */}
      <div className="flex items-center gap-1" data-testid="cust-list-status-tabs">
        {[
          { k: '', label: '전체' },
          { k: 'normal', label: '정상' },
          { k: 'blocked', label: '차단' },
        ].map((t) => (
          <button
            key={t.k || 'all'}
            onClick={() => setStatusFilter(t.k)}
            className={`px-3 py-1.5 rounded-full text-[11px] font-black transition-colors ${
              statusFilter === t.k ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10 text-gray-600 hover:bg-black/10'
            }`}
            data-testid={`cust-list-status-${t.k || 'all'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 text-center text-xs text-gray-500 inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={14} className="animate-spin" /> 불러오는 중…
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-xs text-gray-500">고객이 없습니다.</div>
      ) : (
        <ul className="grid gap-2" data-testid="cust-list">
          {rows.map((c) => (
            <li key={c.customer_key}>
              <button onClick={() => onOpen(c.customer_key)}
                data-testid={`cust-row-${c.customer_key}`}
                className="w-full text-left p-3 rounded-2xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/5 transition">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-black text-sm truncate">{c.name || '이름 미상'}</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${c.is_member ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 dark:bg-white/10'}`}>
                      {c.is_member ? '회원' : '비회원'}
                    </span>
                    {(c.sources || []).map((s) => (
                      <span key={s} className="text-[9px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{s}</span>
                    ))}
                    {c.unanswered > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white font-bold">
                        미처리 {c.unanswered}
                      </span>
                    )}
                    {c.is_blocked && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-black text-white font-bold inline-flex items-center gap-0.5"
                            data-testid={`cust-row-blocked-${c.customer_key}`}>
                        <Ban size={9}/> 차단됨
                      </span>
                    )}
                  </div>
                  <ChevronRight size={14} className="text-gray-400 shrink-0" />
                </div>
                <div className="mt-1 text-[11px] text-gray-500 flex flex-wrap gap-x-3">
                  <span data-testid="cust-list-phone" title="목록에서는 개인정보 보호를 위해 마스킹됩니다. 상세 화면에서 원본 확인 가능.">
                    {c.phone ? maskPhone(c.phone) : '연락처 없음'}
                  </span>
                  {c.email && <span>{c.email}</span>}
                  <span>주문 {c.orders_count}건 · {won(c.total_amount)}</span>
                  <span>최근 {fmtDate(c.recent_at)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Customer Orders Tab (Phase 12) ────────────────────────────────────────
// Renders the customer's order history inside CustomerDetailDrawer. Clicking a
// row opens the full <SellerOrderDetail /> modal that already exists in the
// SellerOrdersSection so all status/invoice/rollback actions stay identical.
function CustomerOrdersTab({ orders }) {
  const [openId, setOpenId] = useState(null); // seller_order_id

  if (!orders || orders.length === 0) {
    return (
      <div className="py-8 text-center text-xs text-gray-500" data-testid="cust-detail-orders-empty">
        주문 이력이 없습니다.
      </div>
    );
  }

  const firstItem = (o) => {
    const items = o.items || [];
    if (items.length === 0) return { name: '상품 정보 없음', more: 0, qty: 1 };
    const it = items[0] || {};
    return {
      name: it.name_snapshot || it.product_name || it.name || '상품명 없음',
      more: Math.max(0, items.length - 1),
      qty: it.quantity || 1,
    };
  };

  return (
    <>
      <ul className="grid gap-2" data-testid="cust-detail-orders">
        {orders.map((o) => {
          const it = firstItem(o);
          const sid = o.seller_order_id || o.order_id;
          const status = o.status_label || ORDER_STATUS_LABEL[o.status] || o.status || '-';
          const total = o.totals?.total ?? o.total ?? 0;
          const tracking = o.shipment?.tracking_number || o.tracking_number || '';
          return (
            <li key={sid}>
              <button
                onClick={() => setOpenId(sid)}
                data-testid={`cust-detail-order-${sid}`}
                className="w-full text-left p-3 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40 hover:bg-[#FF3B30]/5 transition"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-black text-sm truncate">{o.order_number || '-'}</p>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{status}</span>
                      {tracking && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 font-mono">
                          {tracking}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 truncate inline-flex items-center gap-1">
                      <Package size={10} />
                      {it.name} × {it.qty}
                      {it.more > 0 && <span className="text-gray-400"> 외 {it.more}건</span>}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">{fmtDate(o.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-black text-[#FF3B30]">{won(total)}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">상세 &gt;</p>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {openId && (
        <SellerOrderDetail id={openId} onClose={() => setOpenId(null)} onChanged={() => {}} />
      )}
    </>
  );
}

// ── Customer Detail Drawer ────────────────────────────────────────────────
function CustomerDetailDrawer({ customerKey, onClose, onOpenInquiry }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('orders');
  const [busy, setBusy] = useState(false);
  const [newMemo, setNewMemo] = useState('');
  const [showAsk, setShowAsk] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false); // STEP 5-CB
  const [blockMemo, setBlockMemo] = useState('');

  const load = useCallback(async () => {
    try { const r = await api.get('/seller/customers/detail', { params: { key: customerKey } }); setD(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || '고객 상세 로드 실패'); onClose(); }
  }, [customerKey, onClose]);
  useEffect(() => { load(); }, [load]);

  const addMemo = async () => {
    if (!newMemo.trim()) return;
    setBusy(true);
    try {
      await api.post('/seller/customers/memos', { customer_key: customerKey, memo: newMemo.trim() });
      setNewMemo(''); await load(); toast.success('메모 추가됨');
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  const doBlock = async () => {
    setBusy(true);
    try {
      await api.post('/seller/customers/block', { customer_key: customerKey, memo: blockMemo.trim() });
      setShowBlockConfirm(false); setBlockMemo(''); await load(); toast.success('고객을 차단했습니다.');
    } catch (e) { toast.error(e?.response?.data?.detail || '차단 실패'); }
    finally { setBusy(false); }
  };

  const doUnblock = async () => {
    setBusy(true);
    try {
      await api.post('/seller/customers/unblock', { customer_key: customerKey });
      await load(); toast.success('차단이 해제되었습니다.');
    } catch (e) { toast.error(e?.response?.data?.detail || '해제 실패'); }
    finally { setBusy(false); }
  };

  if (!d) return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center" onClick={onClose}>
      <Loader2 size={20} className="animate-spin text-white" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex" onClick={onClose} data-testid="cust-detail-drawer">
      <div className="ml-auto w-full sm:max-w-2xl bg-white dark:bg-[#0A0A0A] h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 pt-6 border-b border-black/10 dark:border-white/10 flex items-start justify-between shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-black" data-testid="cust-detail-name">{d.name || '이름 미상'}</h3>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${d.is_member ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 dark:bg-white/10'}`}>
                {d.is_member ? '회원' : '비회원'}
              </span>
              {(d.sources || []).map((s) => <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{s}</span>)}
              {d.is_blocked && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-black text-white font-black inline-flex items-center gap-1"
                      data-testid="cust-detail-blocked-badge">
                  <Ban size={10}/> 차단됨
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] text-gray-500 flex gap-3 flex-wrap">
              {d.user_id && <span className="font-mono">{d.user_id}</span>}
              {d.phone && <span className="inline-flex items-center gap-1"><Phone size={10} />{d.phone}</span>}
              {d.email && <span className="inline-flex items-center gap-1"><Mail size={10} />{d.email}</span>}
              <span>최근 {fmtDate(d.recent_at)}</span>
            </div>
            <div className="mt-2 flex gap-3 text-[10px] text-gray-500 flex-wrap" data-testid="cust-detail-summary">
              <span>총 주문 <b className="text-black dark:text-white">{d.orders_count}건</b></span>
              <span>구매금액 <b className="text-[#FF3B30]">{won(d.total_amount)}</b></span>
              <span>취소 {d.status_counts?.cancelled || 0}</span>
              <span>환불 {d.status_counts?.refunded || 0}</span>
              <span>미처리 문의 <b className="text-[#FF3B30]">{(d.inquiry_counts?.new || 0) + (d.inquiry_counts?.in_progress || 0)}</b></span>
              {/* Prod-CS-Polish · RMA 유형별 breakdown (order_rma_requests 재사용) */}
              <span data-testid="cust-detail-rma-counts" title="취소·교환·반품 진행 이력 (철회 제외)">
                RMA <b className="text-black dark:text-white">{d.rma_counts?.total || 0}</b>
                <span className="text-gray-400 ml-1">
                  (취소 {d.rma_counts?.cancel || 0} · 교환 {d.rma_counts?.exchange || 0} · 반품 {d.rma_counts?.return || 0})
                </span>
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 shrink-0"><X size={16} /></button>
        </div>

        {/* STEP 5-CB · 차단/해제 액션 · 확인창 · 명확하지만 과도하지 않은 UI */}
        <div className="px-4 py-2 border-b border-black/10 dark:border-white/10 shrink-0 flex items-center gap-2 flex-wrap" data-testid="cust-detail-block-bar">
          {d.is_blocked ? (
            <>
              <span className="text-[11px] text-gray-500">이 고객은 판매자님이 <b className="text-black dark:text-white">차단</b>한 상태입니다.</span>
              <button onClick={doUnblock} disabled={busy}
                      className="ml-auto px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid="cust-detail-unblock-btn">
                <ShieldOff size={12}/> 차단 해제
              </button>
            </>
          ) : (
            <>
              <span className="text-[11px] text-gray-500">필요 시 이 고객을 <b className="text-black dark:text-white">차단</b>할 수 있습니다.</span>
              <button onClick={() => setShowBlockConfirm(true)} disabled={busy}
                      className="ml-auto px-3 py-1.5 rounded-full bg-black hover:bg-gray-800 text-white text-xs font-black inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid="cust-detail-block-btn">
                <Ban size={12}/> 고객 차단
              </button>
            </>
          )}
        </div>

        <div className="flex gap-1 px-4 border-b border-black/10 dark:border-white/10 bg-white dark:bg-[#0A0A0A] shrink-0 overflow-x-auto">
          {[
            { k: 'orders', label: `주문 ${d.orders?.length || 0}` },
            { k: 'inquiries', label: `문의 ${d.inquiries?.length || 0}` },
            { k: 'addresses', label: `배송지 ${d.addresses?.length || 0}` },
            { k: 'memos', label: `CS 메모 ${d.memos?.length || 0}` },
            { k: 'history', label: `이력 ${d.history?.length || 0}` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`cust-detail-tab-${t.k}`}
              className={`px-3 py-2 text-[11px] font-bold border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tab === 'orders' && (
            <CustomerOrdersTab orders={d.orders || []} />
          )}

          {tab === 'inquiries' && (
            <div className="space-y-2" data-testid="cust-detail-inquiries">
              <button onClick={() => setShowAsk(true)} className="w-full py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold" data-testid="cust-detail-add-inquiry">
                + 판매자 대신 문의 등록
              </button>
              {(d.inquiries || []).map((i) => (
                <button key={i.inquiry_id} onClick={() => onOpenInquiry(i.inquiry_id)}
                  data-testid={`cust-detail-inquiry-${i.inquiry_id}`}
                  className="w-full text-left p-3 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40 text-xs">
                  <div className="flex justify-between items-start">
                    <div className="min-w-0">
                      <p className="font-black truncate">{i.title}</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{CATEGORY_LABEL[i.category]} · {fmtDate(i.created_at)}</p>
                      {i.order_number && <p className="text-[10px] text-gray-500">주문 {i.order_number}</p>}
                    </div>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 shrink-0">{STATUS_LABEL[i.status]}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {tab === 'addresses' && (
            <ul className="grid gap-2" data-testid="cust-detail-addresses">
              {(d.addresses || []).map((a, i) => (
                <li key={i} className="p-3 rounded-xl border border-black/10 dark:border-white/10 text-xs">
                  <p className="font-black inline-flex items-center gap-1"><MapPin size={11} />{a.recipient_name}</p>
                  <p className="text-[11px] text-gray-500">{a.phone}</p>
                  <p className="text-[11px] mt-1">({a.postal_code}) {a.address1} {a.address2}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">최근 사용 {fmtDate(a.last_used)}</p>
                </li>
              ))}
            </ul>
          )}

          {tab === 'memos' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input value={newMemo} onChange={(e) => setNewMemo(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addMemo()}
                  placeholder="내부용 CS 메모 (소비자에게 노출되지 않음)"
                  className="flex-1 px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
                  data-testid="cust-detail-memo-input" />
                <button onClick={addMemo} disabled={busy || !newMemo.trim()} data-testid="cust-detail-memo-add"
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-black text-white disabled:opacity-30">추가</button>
              </div>
              <ul className="grid gap-2">
                {(d.memos || []).map((m) => (
                  <li key={m.memo_id} className="p-3 rounded-xl border border-black/10 dark:border-white/10 text-xs">
                    <p>{m.memo}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{m.created_by?.name} · {fmtDate(m.created_at)}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {tab === 'history' && (
            <ul className="grid gap-1 text-[11px]" data-testid="cust-detail-history">
              {(d.history || []).map((h) => (
                <li key={h.history_id} className="p-2 rounded border border-black/5 dark:border-white/10">
                  <span className="font-mono text-[10px] text-gray-500">{fmtDate(h.created_at)}</span>{' '}
                  <b>{h.event}</b>{' · '}<span className="text-gray-500">{h.actor?.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {showAsk && (
          <InquiryComposer
            customerKey={customerKey}
            defaultBuyer={{ name: d.name, phone: d.phone, email: d.email }}
            recentOrders={d.orders || []}
            onClose={() => setShowAsk(false)}
            onCreated={() => { setShowAsk(false); load(); }}
          />
        )}

        {/* STEP 5-CB · 차단 확인 모달 · 차단 사유(memo) 입력 */}
        {showBlockConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
               onClick={() => !busy && setShowBlockConfirm(false)}
               data-testid="cust-block-confirm-modal">
            <div className="w-full max-w-md bg-white dark:bg-[#0A0A0A] rounded-2xl shadow-xl p-5"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-2">
                <Ban size={16} className="text-[#FF3B30]" />
                <h3 className="text-base font-black">고객을 차단하시겠습니까?</h3>
              </div>
              <p className="text-[12px] text-gray-600 dark:text-gray-300 leading-relaxed">
                차단 후 이 고객은 판매자님의 상품을 <b className="text-black dark:text-white">새로 구매할 수 없고</b>,
                방송/마케팅 알림 대상에서 제외됩니다.<br/>
                기존 주문·배송·CS·교환·반품·환불 기록은 그대로 유지됩니다.
              </p>
              <div className="mt-3">
                <label className="text-[11px] font-bold text-gray-500 block mb-1">차단 사유 <span className="text-gray-400 font-normal">(내부 관리용 · 소비자에게 노출되지 않음)</span></label>
                <textarea value={blockMemo} onChange={(e) => setBlockMemo(e.target.value)}
                  rows={3} maxLength={500}
                  placeholder="예: 반복 취소, 악성 문의, 구매 거부 사유 등"
                  className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent resize-none"
                  data-testid="cust-block-memo-input" />
                <p className="text-[10px] text-gray-400 mt-0.5">{blockMemo.length} / 500</p>
              </div>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={() => { setShowBlockConfirm(false); setBlockMemo(''); }}
                        disabled={busy}
                        className="px-4 py-2 rounded-full text-xs font-bold border border-black/10 dark:border-white/10 disabled:opacity-50"
                        data-testid="cust-block-cancel-btn">
                  취소
                </button>
                <button onClick={doBlock} disabled={busy}
                        className="px-4 py-2 rounded-full text-xs font-black bg-black hover:bg-gray-800 text-white inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid="cust-block-confirm-btn">
                  {busy ? <Loader2 size={12} className="animate-spin"/> : <Ban size={12}/>}
                  고객 차단
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Inquiry List (tab 2) ──────────────────────────────────────────────────
function InquiryList({ onOpen, onOpenCustomer }) {
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, new: 0, in_progress: 0, answered: 0, closed: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get('/seller/inquiries', { params: { status: status || undefined, category: category || undefined, q: q || undefined } }),
        api.get('/seller/inquiries/summary'),
      ]);
      setRows(l.data || []); setSummary(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '문의 조회 실패'); }
    finally { setLoading(false); }
  }, [status, category, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-5 gap-2 text-center text-[10px]" data-testid="inq-summary">
        {[
          { k: '', label: '전체', v: summary.total },
          { k: 'new', label: '신규', v: summary.new },
          { k: 'in_progress', label: '처리중', v: summary.in_progress },
          { k: 'answered', label: '답변완료', v: summary.answered },
          { k: 'closed', label: '종료', v: summary.closed },
        ].map((s) => (
          <button key={s.label} onClick={() => setStatus(s.k)}
            data-testid={`inq-summary-${s.k || 'all'}`}
            className={`p-2 rounded-lg border ${status === s.k ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}>
            <p className="text-gray-500">{s.label}</p>
            <p className="font-black text-sm">{s.v}</p>
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="inq-filter-category"
          className="px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent">
          <option value="">전체 유형</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="고객명 · 휴대폰 · 주문번호 · 내용"
          className="flex-1 min-w-[200px] px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
          data-testid="inq-search" />
        <button onClick={load} className="px-3 py-2 text-xs font-bold rounded-lg bg-black text-white">검색</button>
      </div>

      {loading ? <p className="text-center text-xs text-gray-500 py-8"><Loader2 size={14} className="animate-spin inline" /></p>
        : rows.length === 0 ? <p className="text-center text-xs text-gray-500 py-8">문의가 없습니다.</p>
        : (
          <ul className="grid gap-2" data-testid="inq-list">
            {rows.map((i) => (
              <li key={i.inquiry_id}>
                <button onClick={() => onOpen(i.inquiry_id)} data-testid={`inq-row-${i.inquiry_id}`}
                  className="w-full text-left p-3 rounded-xl border border-black/10 dark:border-white/10 hover:border-[#FF3B30]/40">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex gap-1 items-center flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold">{CATEGORY_LABEL[i.category]}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10">{STATUS_LABEL[i.status]}</span>
                        {i.order_number && <button onClick={(e) => { e.stopPropagation(); onOpenCustomer(i.customer_key); }} className="text-[10px] text-gray-500 hover:text-[#FF3B30]">주문 {i.order_number}</button>}
                      </div>
                      <p className="text-sm font-black mt-1 truncate">{i.title}</p>
                      <p className="text-[11px] text-gray-500 truncate">{(i.buyer_snapshot?.name) || '고객'} · {fmtDate(i.created_at)}</p>
                    </div>
                    <ChevronRight size={14} className="text-gray-400 shrink-0" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

// ── Inquiry Detail Drawer ─────────────────────────────────────────────────
function InquiryDetailDrawer({ inquiryId, onClose, onOpenCustomer }) {
  const [d, setD] = useState(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/seller/inquiries/${inquiryId}`); setD(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || '문의 로드 실패'); onClose(); }
  }, [inquiryId, onClose]);
  useEffect(() => { load(); }, [load]);

  const answer = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/seller/inquiries/${inquiryId}/answer`, { content: reply.trim() }); setReply(''); await load(); toast.success('답변 등록됨'); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    setBusy(true);
    try { await api.patch(`/seller/inquiries/${inquiryId}/status`, { status }); await load(); toast.info('상태 변경'); }
    catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={onClose}><Loader2 size={20} className="animate-spin text-white" /></div>;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex" onClick={onClose} data-testid="inq-detail-drawer">
      <div className="ml-auto w-full sm:max-w-xl bg-white dark:bg-[#0A0A0A] h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 pt-6 border-b border-black/10 dark:border-white/10 flex items-start justify-between shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500">{CATEGORY_LABEL[d.category]} · {STATUS_LABEL[d.status]}</p>
            <h3 className="text-lg font-black mt-1" data-testid="inq-detail-title">{d.title}</h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              <button onClick={() => d.customer_key && onOpenCustomer(d.customer_key)} className="hover:text-[#FF3B30]" data-testid="inq-detail-open-customer">
                {d.buyer_snapshot?.name || '고객'}
              </button>
              {d.buyer_snapshot?.phone && ` · ${d.buyer_snapshot.phone}`}
              {` · ${fmtDate(d.created_at)}`}
            </p>
          </div>
          <button onClick={onClose} className="p-1"><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          {d.related_order && (
            <div className="p-3 rounded-xl border border-orange-500/30 bg-orange-500/5 text-xs" data-testid="inq-related-order">
              <p className="font-black text-orange-700 dark:text-orange-400 mb-1">관련 주문 · {d.related_order.order_number || '-'}</p>
              <p className="text-[11px]">{
                (d.related_order.items || []).length === 0
                  ? '-'
                  : (d.related_order.items || []).map((it) =>
                      `${it.name_snapshot || it.product_name || it.name || '상품명 없음'} × ${it.quantity || 1}`
                    ).join(' · ')
              }</p>
              <div className="mt-1 flex gap-3 flex-wrap text-[11px] text-gray-500">
                <span>결제금액 <b className="text-[#FF3B30]">{won(d.related_order.totals?.total ?? d.related_order.total ?? 0)}</b></span>
                <span>결제 <b>{ORDER_STATUS_LABEL[d.related_order.status] || d.related_order.status || '-'}</b></span>
                {(d.related_order.shipment?.courier || d.related_order.courier) && (
                  <span>택배사 <b>{d.related_order.shipment?.courier || d.related_order.courier}</b></span>
                )}
                {(d.related_order.shipment?.tracking_number || d.related_order.tracking_number) && (
                  <span>송장 <span className="font-mono">{d.related_order.shipment?.tracking_number || d.related_order.tracking_number}</span></span>
                )}
                {/* STEP 5-CT-C · 발송/배송일 · delivery_status · 기존 필드만 REUSE, 있을 때만 표시 */}
                {d.related_order.shipment?.shipped_at && (
                  <span>발송일 <b>{fmtDate(d.related_order.shipment.shipped_at)}</b></span>
                )}
                {d.related_order.shipment?.delivered_at && (
                  <span className="text-emerald-600">배송완료 <b>{fmtDate(d.related_order.shipment.delivered_at)}</b></span>
                )}
                {d.related_order.delivery_status && (
                  <span data-testid="inq-related-delivery-status">배송상태 <b>{d.related_order.delivery_status}</b></span>
                )}
              </div>
              {/* STEP 5-CT-C · 다중 배송 (Phase 2.7 shipments[]) · 있을 때만 노출 */}
              {Array.isArray(d.related_order.shipments) && d.related_order.shipments.length > 1 && (
                <div className="mt-2 space-y-1" data-testid="inq-related-shipments">
                  {d.related_order.shipments.map((sh, si) => (
                    <div key={sh.shipment_id || si} className="text-[10px] text-gray-500 px-2 py-1 rounded bg-black/[0.03] dark:bg-white/[0.05]">
                      배송 {sh.shipment_no || si + 1} · <b>{sh.courier || '-'}</b>
                      {sh.tracking_number && <> · <span className="font-mono">{sh.tracking_number}</span></>}
                      {sh.status && <> · {sh.status}</>}
                    </div>
                  ))}
                </div>
              )}
              {/* STEP 5-CT-D · 배송조회 준비 UI · 외부 API 미연동 · 공식 URL 매핑만 */}
              {(d.related_order.shipment?.courier || d.related_order.shipment?.tracking_number) && (() => {
                const sh = d.related_order.shipment || {};
                const status = getTrackingStatus({ shipment: sh, delivery_status: d.related_order.delivery_status, orderStatus: d.related_order.status });
                const available = getTrackingAvailable(sh.courier, sh.tracking_number);
                const url = available ? getTrackingUrl(sh.courier, sh.tracking_number) : null;
                return (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="inq-related-tracking">
                    {status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-bold" data-testid="inq-related-tracking-status">{status}</span>}
                    {available && url ? (
                      <a href={url} target="_blank" rel="noreferrer noopener"
                         className="text-[11px] font-bold text-[#FF3B30] underline underline-offset-2 hover:text-[#e0332a]"
                         data-testid="inq-related-tracking-link">
                        배송조회 열기 ↗
                      </a>
                    ) : (
                      <span className="text-[10px] text-gray-400" data-testid="inq-related-tracking-pending">
                        배송조회 준비중
                      </span>
                    )}
                  </div>
                );
              })()}
              {d.related_order.shipping_address_snapshot && (
                <p className="text-[11px] text-gray-500 mt-1">
                  <MapPin size={10} className="inline" /> {d.related_order.shipping_address_snapshot.recipient_name ? `${d.related_order.shipping_address_snapshot.recipient_name} · ` : ''}
                  {d.related_order.shipping_address_snapshot.address1 || ''} {d.related_order.shipping_address_snapshot.address2 || ''}
                </p>
              )}
            </div>
          )}

          <div className="p-3 rounded-xl border border-black/10 dark:border-white/10">
            <p className="text-xs whitespace-pre-wrap">{d.content}</p>
          </div>

          {(d.answers || []).map((a) => (
            <div key={a.answer_id} className="p-3 rounded-xl bg-[#FF3B30]/5 border border-[#FF3B30]/20">
              <p className="text-[10px] font-bold text-[#FF3B30]">판매자 답변 · {fmtDate(a.created_at)}</p>
              <p className="text-xs mt-1 whitespace-pre-wrap">{a.content}</p>
            </div>
          ))}

          <div className="space-y-2 pt-2 border-t border-black/10 dark:border-white/10">
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="답변 작성…"
              className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
              data-testid="inq-detail-reply" />
            <div className="flex gap-2 flex-wrap">
              <button onClick={answer} disabled={busy || !reply.trim()} data-testid="inq-detail-answer-btn"
                className="flex-1 py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-30 inline-flex items-center gap-1 justify-center">
                <Send size={12} /> 답변 등록
              </button>
              <button onClick={() => setStatus('in_progress')} disabled={busy} className="px-3 py-2 rounded-full border border-black/10 dark:border-white/10 text-[11px]" data-testid="inq-detail-set-inprogress">처리중</button>
              <button onClick={() => setStatus('closed')} disabled={busy} className="px-3 py-2 rounded-full border border-black/10 dark:border-white/10 text-[11px]" data-testid="inq-detail-set-closed">종료</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Composer (seller creates inquiry on behalf of customer) ────────────────
function InquiryComposer({ customerKey, defaultBuyer, recentOrders = [], onClose, onCreated }) {
  const [category, setCategory] = useState('other');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [orderId, setOrderId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !content.trim()) { toast.error('제목/내용 필수'); return; }
    setBusy(true);
    try {
      await api.post('/seller/inquiries', {
        customer_key: customerKey, category,
        title: title.trim(), content: content.trim(),
        order_id: orderId || undefined,
        buyer_snapshot: defaultBuyer,
      });
      toast.success('문의 등록');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white dark:bg-[#0A0A0A] rounded-2xl p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <p className="font-black">문의 등록</p>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="composer-category">
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={orderId} onChange={(e) => setOrderId(e.target.value)} className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="composer-order">
          <option value="">관련 주문 없음</option>
          {recentOrders.slice(0, 20).map((o) => <option key={o.order_id} value={o.order_id}>{o.order_number} · {won(o.total)}</option>)}
        </select>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="제목" className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="composer-title" />
        <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4} placeholder="문의 내용" className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent" data-testid="composer-content" />
        <button onClick={submit} disabled={busy} className="w-full py-2 rounded-full bg-[#FF3B30] text-white text-xs font-bold disabled:opacity-30" data-testid="composer-submit">등록</button>
      </div>
    </div>
  );
}

// ── Global CS history (tab 3) ─────────────────────────────────────────────
function HistoryList() {
  // No dedicated global-history endpoint yet — reuse recent inquiries + fold in
  // memos to give sellers a chronological CS activity feed. Cheap read.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const inq = await api.get('/seller/inquiries', { params: { limit: 200 } });
        const rows = [];
        (inq.data || []).forEach((i) => {
          rows.push({ t: i.created_at, e: `문의접수 · ${CATEGORY_LABEL[i.category]} · ${i.title}`, key: `c-${i.inquiry_id}`, meta: i });
          (i.answers || []).forEach((a) => rows.push({ t: a.created_at, e: `답변등록 · ${i.title}`, key: `a-${a.answer_id}`, meta: i }));
          if (i.answered_at) rows.push({ t: i.answered_at, e: `상태변경 · answered · ${i.title}`, key: `s-${i.inquiry_id}`, meta: i });
        });
        rows.sort((a, b) => new Date(b.t) - new Date(a.t));
        setItems(rows.slice(0, 200));
      } catch (e) { toast.error('이력 로드 실패'); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <p className="text-center text-xs text-gray-500 py-8"><Loader2 size={14} className="animate-spin inline" /></p>;
  return (
    <ul className="grid gap-1 text-[11px]" data-testid="cs-history-list">
      {items.map((r) => (
        <li key={r.key} className="p-2 rounded border border-black/5 dark:border-white/10">
          <span className="font-mono text-[10px] text-gray-500">{fmtDate(r.t)}</span>
          {' · '}<span>{r.e}</span>
        </li>
      ))}
      {items.length === 0 && <li className="text-center text-gray-500 py-8">이력이 없습니다.</li>}
    </ul>
  );
}
